import React, { useEffect, useState } from 'react';
import {
  Button,
  Confirm,
  Header,
  Label,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const ConsumptionInvoiceSection = () => {
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState('');
  const [months, setMonths] = useState([]);
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(null); // month being submitted
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMonth, setConfirmMonth] = useState(null);
  const [confirmAmount, setConfirmAmount] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, monthsRes, requestsRes] = await Promise.all([
        API.get('/api/invoice/profile'),
        API.get('/api/consumption_invoice/available_months'),
        API.get('/api/consumption_invoice/requests'),
      ]);
      if (profileRes.data.success && profileRes.data.data) {
        setProfile(profileRes.data.data);
      }
      if (monthsRes.data.success) {
        setMonths(monthsRes.data.data || []);
      }
      if (requestsRes.data.success) {
        setRequests(requestsRes.data.data || []);
      }
    } catch (_) {}
    // Get user email from localStorage
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.email) setEmail(user.email);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (month, amountFen) => {
    setConfirmMonth(month);
    setConfirmAmount(amountFen);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    const month = confirmMonth;
    setSubmitting(month);
    try {
      const res = await API.post('/api/consumption_invoice/request', { month });
      const { success, message } = res.data;
      if (success) {
        showSuccess('发票申请已提交');
        loadData();
      } else {
        showError(message);
      }
    } catch (_) {
      showError('提交失败');
    } finally {
      setSubmitting(null);
    }
  };

  const handleDownload = (month) => {
    window.open(`/api/consumption_invoice/download?month=${month}`, '_blank');
  };

  const formatAmount = (fen) => `¥${(fen / 100).toFixed(2)}`;
  const quotaToYuan = (quota) => {
    const quotaPerUnit = parseFloat(localStorage.getItem('quota_per_unit')) || 500000;
    return `¥${(quota / quotaPerUnit).toFixed(4)}`;
  };

  const statusLabel = (status) => {
    if (status === 1) return <Label color='yellow' size='small'>待处理</Label>;
    if (status === 2) return <Label color='green' size='small'>已开票</Label>;
    return <Label size='small'>未知</Label>;
  };

  if (loading) return null;

  return (
    <Segment>
      <Header as='h3'>消费发票申请</Header>
      {!profile ? (
        <Message warning>
          请先在「设置」页面的「发票信息」中填写发票抬头和税号。
        </Message>
      ) : (
        <Message info size='small'>
          发票抬头：{profile.title}，税号：{profile.tax_number}
          {email && <span>。发票将发送至您的账户邮箱：<strong>{email}</strong></span>}
          {!email && <span>。请先在「设置」中绑定邮箱以接收发票。</span>}
        </Message>
      )}

      {months.length > 0 && profile && (
        <>
          <Header as='h4'>可开票月份</Header>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>月份</Table.HeaderCell>
                <Table.HeaderCell>消费额度</Table.HeaderCell>
                <Table.HeaderCell>兑换码抵扣</Table.HeaderCell>
                <Table.HeaderCell>可开票金额</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {months.map((item) => (
                <Table.Row key={item.month}>
                  <Table.Cell>{item.month}</Table.Cell>
                  <Table.Cell>{quotaToYuan(item.consume_quota)}</Table.Cell>
                  <Table.Cell>{quotaToYuan(item.redemption_quota)}</Table.Cell>
                  <Table.Cell><strong>{formatAmount(item.amount_fen)}</strong></Table.Cell>
                  <Table.Cell>
                    <Button
                      size='small'
                      primary
                      loading={submitting === item.month}
                      disabled={!!submitting}
                      onClick={() => handleSubmit(item.month, item.amount_fen)}
                    >
                      申请开票
                    </Button>
                    <Button
                      size='small'
                      basic
                      onClick={() => handleDownload(item.month)}
                    >
                      下载明细
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      {months.length === 0 && profile && (
        <Message size='small'>暂无可开票的月份。</Message>
      )}

      {requests.length > 0 && (
        <>
          <Header as='h4' style={{ marginTop: '2em' }}>申请记录</Header>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>月份</Table.HeaderCell>
                <Table.HeaderCell>金额</Table.HeaderCell>
                <Table.HeaderCell>状态</Table.HeaderCell>
                <Table.HeaderCell>备注</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {requests.map((req) => (
                <Table.Row key={req.id}>
                  <Table.Cell>{req.month}</Table.Cell>
                  <Table.Cell>{formatAmount(req.total_amount_fen)}</Table.Cell>
                  <Table.Cell>{statusLabel(req.status)}</Table.Cell>
                  <Table.Cell>{req.admin_remark || '-'}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size='small'
                      basic
                      onClick={() => handleDownload(req.month)}
                    >
                      下载明细
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
      <Confirm
        open={confirmOpen}
        content={`确认申请 ${confirmMonth} 的发票，金额 ¥${(confirmAmount / 100).toFixed(2)}？`}
        confirmButton='确认'
        cancelButton='取消'
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </Segment>
  );
};

export default ConsumptionInvoiceSection;
